import { ReactComponent as LocationIcon } from 'assets/image/svg/location.svg';
import mapboxgl from 'mapbox-gl';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { INITIAL_ZOOM } from 'src/utils/constants';
import 'mapbox-gl/dist/mapbox-gl.css';

type SimpleMapViewProps = {
   iconSize?: number;
   id: string;
   interactive?: boolean;
   latitude: number;
   longitude: number;
   zoom?: number;
   hideAttControl?: boolean;
   markerColor?: string;
};

const SimpleMapView: React.FC<SimpleMapViewProps> = ({
   iconSize,
   id,
   interactive,
   latitude,
   longitude,
   zoom = INITIAL_ZOOM,
   hideAttControl = false,
   markerColor,
}) => {
   // Guarding with an early return above the hooks made every hook in this
   // component conditional (react-hooks/rules-of-hooks): a coordinate arriving
   // late changes the hook count between renders, which corrupts React's hook
   // state. Compute the condition here, bail inside each effect, and keep the
   // early return below — after every hook has been called (NPT-1570).
   const hasCoordinates = !!latitude && !!longitude;

   const mapRef: any = useRef(null);
   const mapContainerRef: any = useRef(null);

   useEffect(() => {
      if (!hasCoordinates) return;

      mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
      mapRef.current = new mapboxgl.Map({
         container: mapContainerRef.current,
         center: [longitude, latitude],
         zoom: zoom,
         interactive: !!interactive,
         attributionControl: !hideAttControl,
      });

      const el = document.createElement('div');
      el.id = id;

      if (iconSize) {
         const root = ReactDOM.createRoot(el);
         root.render(<LocationIcon className={markerColor ? markerColor : 'text-orgPrimary'} width={iconSize} height={iconSize} />);
      }

      new mapboxgl.Marker(el).setLngLat([longitude, latitude]).addTo(mapRef.current!);

      return () => {
         mapRef.current.remove();
      };
   }, [latitude, longitude, hasCoordinates]);

   useEffect(() => {
      if (!hasCoordinates) return;

      const center = [longitude, latitude];
      mapRef?.current?.flyTo({ center, zoom: INITIAL_ZOOM, duration: 0 });
   }, [latitude, longitude, hasCoordinates]);

   if (!hasCoordinates) return null;

   return <div className="flex-1 h-full min-h-[130px] w-full" id="map-container" ref={mapContainerRef} />;
};

export default SimpleMapView;
